export const IMAGE_HISTORY_LIMIT = 20;

export type ImageHistoryEntryKind = 'upload' | 'generated' | 'edited';

export interface ImageHistoryEntry {
  id: string;
  blob: Blob;
  createdAt: number;
  height: number;
  kind: ImageHistoryEntryKind;
  prompt: string | null;
  sourceId: string | null;
  width: number;
}

export interface ImageHistorySnapshot {
  entries: readonly ImageHistoryEntry[];
  selectedId: string | null;
}

interface PersistedImageHistoryEntry {
  id: string;
  createdAt: number;
  height: number;
  imageBytes: ArrayBuffer;
  kind: ImageHistoryEntryKind;
  mediaType: string;
  prompt: string | null;
  sourceId: string | null;
  width: number;
}

export class ImageHistory {
  private readonly items: ImageHistoryEntry[];
  private cursorIndex: number;

  constructor(entries: readonly ImageHistoryEntry[] = [], selectedId: string | null = null) {
    this.items = [...entries];
    const selectedIndex = selectedId ? this.items.findIndex((entry) => entry.id === selectedId) : -1;
    this.cursorIndex = selectedIndex >= 0 ? selectedIndex : this.items.length - 1;
  }

  get entries(): readonly ImageHistoryEntry[] {
    return this.items;
  }

  get current(): ImageHistoryEntry | null {
    return this.cursorIndex >= 0 ? this.items[this.cursorIndex] ?? null : null;
  }

  get cursor(): number {
    return this.cursorIndex;
  }

  get canGoBack(): boolean {
    return this.cursorIndex > 0;
  }

  get canGoForward(): boolean {
    return this.cursorIndex >= 0 && this.cursorIndex < this.items.length - 1;
  }

  get snapshot(): ImageHistorySnapshot {
    return {
      entries: [...this.items],
      selectedId: this.current?.id ?? null,
    };
  }

  goBack(): boolean {
    if (!this.canGoBack) {
      return false;
    }
    this.cursorIndex -= 1;
    return true;
  }

  goForward(): boolean {
    if (!this.canGoForward) {
      return false;
    }
    this.cursorIndex += 1;
    return true;
  }

  selectNewest(): boolean {
    if (this.items.length === 0) {
      return false;
    }
    this.cursorIndex = this.items.length - 1;
    return true;
  }

  append(entry: ImageHistoryEntry, selectNew: boolean): string[] {
    const selectedBeforeAppend = this.current?.id ?? null;
    this.items.push(entry);

    const removedIds: string[] = [];
    while (this.items.length > IMAGE_HISTORY_LIMIT) {
      const removableIndex = this.items.findIndex((item) => {
        return item.id !== entry.id && item.id !== selectedBeforeAppend;
      });
      const index = removableIndex >= 0 ? removableIndex : 0;
      const [removed] = this.items.splice(index, 1);
      if (removed) {
        removedIds.push(removed.id);
      }
    }

    if (selectNew) {
      this.cursorIndex = this.items.findIndex((item) => item.id === entry.id);
    } else if (selectedBeforeAppend) {
      this.cursorIndex = this.items.findIndex((item) => item.id === selectedBeforeAppend);
    } else {
      this.cursorIndex = this.items.length - 1;
    }

    return removedIds;
  }

  deleteCurrent(): ImageHistoryEntry | null {
    if (this.cursorIndex < 0) {
      return null;
    }

    const [removed] = this.items.splice(this.cursorIndex, 1);
    if (!removed) {
      return null;
    }

    if (this.items.length === 0) {
      this.cursorIndex = -1;
    } else if (this.cursorIndex >= this.items.length) {
      this.cursorIndex = this.items.length - 1;
    }

    return removed;
  }
}

interface PersistedHistoryState {
  key: 'history';
  entryIds: string[];
  selectedId: string | null;
}

const DATABASE_NAME = 'kai.magic_canvas.v1';
const DATABASE_VERSION = 1;
const ENTRIES_STORE = 'entries';
const STATE_STORE = 'state';

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.addEventListener('success', () => resolve(request.result), { once: true });
    request.addEventListener('error', () => reject(request.error ?? new Error('IndexedDB request failed.')), { once: true });
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.addEventListener('complete', () => resolve(), { once: true });
    transaction.addEventListener('abort', () => reject(transaction.error ?? new Error('IndexedDB transaction aborted.')), { once: true });
    transaction.addEventListener('error', () => reject(transaction.error ?? new Error('IndexedDB transaction failed.')), { once: true });
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isPersistedImageHistoryEntry(value: unknown): value is PersistedImageHistoryEntry {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.id === 'string'
    && typeof value.createdAt === 'number'
    && typeof value.height === 'number'
    && value.imageBytes instanceof ArrayBuffer
    && (value.kind === 'upload' || value.kind === 'generated' || value.kind === 'edited')
    && typeof value.mediaType === 'string'
    && (typeof value.prompt === 'string' || value.prompt === null)
    && (typeof value.sourceId === 'string' || value.sourceId === null)
    && typeof value.width === 'number'
  );
}

function restoreImageHistoryEntry(entry: PersistedImageHistoryEntry): ImageHistoryEntry {
  return {
    id: entry.id,
    blob: new Blob([entry.imageBytes], { type: entry.mediaType }),
    createdAt: entry.createdAt,
    height: entry.height,
    kind: entry.kind,
    prompt: entry.prompt,
    sourceId: entry.sourceId,
    width: entry.width,
  };
}

async function persistImageHistoryEntry(entry: ImageHistoryEntry): Promise<PersistedImageHistoryEntry> {
  return {
    id: entry.id,
    createdAt: entry.createdAt,
    height: entry.height,
    imageBytes: await entry.blob.arrayBuffer(),
    kind: entry.kind,
    mediaType: entry.blob.type || 'image/png',
    prompt: entry.prompt,
    sourceId: entry.sourceId,
    width: entry.width,
  };
}

function isPersistedHistoryState(value: unknown): value is PersistedHistoryState {
  if (!isRecord(value)) {
    return false;
  }

  return (
    value.key === 'history'
    && Array.isArray(value.entryIds)
    && value.entryIds.every((id) => typeof id === 'string')
    && (typeof value.selectedId === 'string' || value.selectedId === null)
  );
}

export class ImageHistoryStore {
  private databasePromise: Promise<IDBDatabase> | null = null;
  private writeQueue: Promise<void> = Promise.resolve();

  async load(): Promise<ImageHistorySnapshot> {
    const database = await this.getDatabase();
    const transaction = database.transaction([ENTRIES_STORE, STATE_STORE], 'readonly');
    const stateRequest = transaction.objectStore(STATE_STORE).get('history');
    const rawState = await requestResult(stateRequest);

    if (!isPersistedHistoryState(rawState)) {
      await transactionDone(transaction);
      return { entries: [], selectedId: null };
    }

    const entryStore = transaction.objectStore(ENTRIES_STORE);
    const entryRequests = rawState.entryIds.map((id) => requestResult(entryStore.get(id)));
    const rawEntries = await Promise.all(entryRequests);
    await transactionDone(transaction);

    return {
      entries: rawEntries.filter(isPersistedImageHistoryEntry).map(restoreImageHistoryEntry),
      selectedId: rawState.selectedId,
    };
  }

  append(
    entry: ImageHistoryEntry,
    snapshot: ImageHistorySnapshot,
    removedIds: readonly string[],
  ): Promise<void> {
    return this.enqueueWrite(async () => {
      const persistedEntry = await persistImageHistoryEntry(entry);
      const database = await this.getDatabase();
      const transaction = database.transaction([ENTRIES_STORE, STATE_STORE], 'readwrite');
      const entryStore = transaction.objectStore(ENTRIES_STORE);
      const writes: Array<Promise<unknown>> = [requestResult(entryStore.put(persistedEntry))];
      for (const removedId of removedIds) {
        writes.push(requestResult(entryStore.delete(removedId)));
      }
      writes.push(requestResult(transaction.objectStore(STATE_STORE).put(this.toPersistedState(snapshot))));
      await Promise.all([Promise.all(writes), transactionDone(transaction)]);
    });
  }

  delete(entryId: string, snapshot: ImageHistorySnapshot): Promise<void> {
    return this.enqueueWrite(async () => {
      const database = await this.getDatabase();
      const transaction = database.transaction([ENTRIES_STORE, STATE_STORE], 'readwrite');
      const writes = [
        requestResult(transaction.objectStore(ENTRIES_STORE).delete(entryId)),
        requestResult(transaction.objectStore(STATE_STORE).put(this.toPersistedState(snapshot))),
      ];
      await Promise.all([Promise.all(writes), transactionDone(transaction)]);
    });
  }

  select(snapshot: ImageHistorySnapshot): Promise<void> {
    return this.enqueueWrite(async () => {
      const database = await this.getDatabase();
      const transaction = database.transaction(STATE_STORE, 'readwrite');
      const write = requestResult(transaction.objectStore(STATE_STORE).put(this.toPersistedState(snapshot)));
      await Promise.all([write, transactionDone(transaction)]);
    });
  }

  close(): void {
    void this.writeQueue.then(async () => {
      const databasePromise = this.databasePromise;
      this.databasePromise = null;
      if (databasePromise) {
        (await databasePromise).close();
      }
    }).catch(() => undefined);
  }

  private toPersistedState(snapshot: ImageHistorySnapshot): PersistedHistoryState {
    return {
      key: 'history',
      entryIds: snapshot.entries.map((entry) => entry.id),
      selectedId: snapshot.selectedId,
    };
  }

  private enqueueWrite(action: () => Promise<void>): Promise<void> {
    const result = this.writeQueue.then(action);
    this.writeQueue = result.catch(() => undefined);
    return result;
  }

  private getDatabase(): Promise<IDBDatabase> {
    if (!this.databasePromise) {
      this.databasePromise = new Promise((resolve, reject) => {
        const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
        request.addEventListener('upgradeneeded', () => {
          const database = request.result;
          if (!database.objectStoreNames.contains(ENTRIES_STORE)) {
            database.createObjectStore(ENTRIES_STORE, { keyPath: 'id' });
          }
          if (!database.objectStoreNames.contains(STATE_STORE)) {
            database.createObjectStore(STATE_STORE, { keyPath: 'key' });
          }
        });
        request.addEventListener('success', () => resolve(request.result), { once: true });
        request.addEventListener('error', () => reject(request.error ?? new Error('Unable to open image history.')), { once: true });
        request.addEventListener('blocked', () => reject(new Error('Image history upgrade is blocked.')), { once: true });
      });
    }
    return this.databasePromise;
  }
}
